<template>
  <div class="manage-item-grid">
    <BaseToggle :checked="managingAccount.enabled"
                class="w-auto" size="xl"
                @change="handleEnabledChange"/>
    <div>
      <div class="text-sm font-medium text-text-secondary">{{ account.name }}</div>
      <div class="text-2sm text-text-ternary">{{ account.account_name }}</div>
    </div>
    <template v-if="managingAccount.enabled">
      <template v-if="initialDisabled">
        <BaseRadio :model-value="managingAccount.connect" :value="false"
                   class="mr-12"
                   @change="managingAccount.connect = $event">
          {{ importTypes[0].label }}
        </BaseRadio>
        <BaseRadio :model-value="managingAccount.connect" :value="true"
                   :disabled="!accountsInCurrency.length"
                   @change="managingAccount.connect = $event">
          {{ importTypes[1].label }}
        </BaseRadio>
      </template>

      <AccountsSelect v-if="managingAccount.connect && managingAccount.enabled"
                      :account_groups="accountGroupsInCurrency"
                      :accounts="accountsInCurrency"
                      :grouped="accountGroupsInCurrency.length > 1"
                      :label="initialDisabled ? $t('accountLabel') : $t('accountLabelAtMK')"
                      :value="managingAccount.connected_account"
                      class="account-select"
                      @input="selectAccount">
        <template #noOptions>
          {{ $t('noAccountsInCurrency') }}
        </template>
      </AccountsSelect>
    </template>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import BaseToggle from "@/components/UI/BaseToggle";
import BaseRadio from "@/components/UI/BaseRadio";
import AccountsSelect from "@/components/UI/BaseSelect/AccountsSelect";

export default {
  name: "ManageBankAccount",
  components: {AccountsSelect, BaseRadio, BaseToggle},
  props: {
    account: {
      type: Object,
      required: true
    },
    accountsInConnectionIds: {
      type: Array,
      required: true
    },
    usedAccountsIds: {
      type: Array
    }
  },
  data() {
    return {
      initialDisabled: true,
      managingAccount: {
        connect: false,
        enabled: false,
        connected_account: null,
      }
    }
  },
  computed: {
    ...mapState('space/accounts', ['accounts']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    importTypes() {
      return [
        {value: false, label: this.$t('addAsNew')},
        {value: true, label: this.$t('joinWithExisting')},
      ]
    },
    accountsInCurrency() {
      return [...this.accounts.data]
          .map(account => this.accountMapper(account))
          .filter(account => this.accountFilter(account))
    },
    accountGroupsInCurrency() {
      return [...this.accounts_by_groups]
          .map(group => ({
            ...group,
            childs: group.childs
                .map(account => this.accountMapper(account))
                .filter(account => this.accountFilter(account))
          }))
          .filter(group => group.childs.length)
    }
  },
  methods: {
    disableAccountSelection(accountId) {
      let isCurrentSelectedAccount = this.managingAccount.connected_account?.id === accountId,
          accountUsedInOtherSelects = this.usedAccountsIds.includes(accountId);
      return !!this.managingAccount.connected_account
          ? !isCurrentSelectedAccount && accountUsedInOtherSelects
          : accountUsedInOtherSelects
    },
    accountMapper(account) {
      return {
        ...account,
        $isDisabled: this.disableAccountSelection(account.id)
      }
    },
    accountFilter(account) {
      let accountWithSameCurrency = account.currency.code === this.account.currency_code,
          accountFromCurrentConnection = !account.import_id || this.accountsInConnectionIds.includes(account.id),
          currentAccount = account.id === this.managingAccount.connected_account?.id;
      return !!this.managingAccount.connected_account
          ? accountFromCurrentConnection && accountWithSameCurrency || currentAccount
          : accountFromCurrentConnection && accountWithSameCurrency
    },
    getPreferredAccount(fromGroup = false) {
      if (fromGroup) {
        let preferredAccount = null;
        for (let group of this.accountGroupsInCurrency) {
          preferredAccount = group.childs.find(acc => !acc.$isDisabled)
          if (preferredAccount) return preferredAccount;
        }
        if (!preferredAccount) return null;
      } else {
        return this.accountsInCurrency.find(acc => !acc.$isDisabled);
      }
    },
    handleEnabledChange(value) {
      this.managingAccount.enabled = value
      if (value && !this.managingAccount.connected_account) {
        let account = this.getPreferredAccount(this.accountGroupsInCurrency.length > 1);
        this.selectAccount(account)
      }
      if (!value && !this.account.account_mk) {
        this.selectAccount(null)
      }
    },
    setAccountManaging() {
      if (this.account.enabled) {
        this.initialDisabled = false;
        this.managingAccount.enabled = true;
      }
      if (this.account.connected_to) this.managingAccount.connected_account = this.account.connected_to
      this.managingAccount.connect = !!this.managingAccount.connected_account;
    },
    selectAccount(value) {
      let oldAccId = !!this.managingAccount.connected_account ? this.managingAccount.connected_account.id : null;
      if (value) this.$emit('selectAccount', [oldAccId, value.id])
      else this.$emit('removeUsedAccount', oldAccId);
      this.managingAccount.connected_account = value;
    },
    getAccountData() {
      return {...this.managingAccount, uid: this.account.uid};
    },
  },
  created() {
    this.setAccountManaging();
  },
  i18n: {
    messages: {
      en: {
        addAsNew: 'Add as new',
        joinWithExisting: 'Join with existing',
        accountLabel: 'Account',
        accountLabelAtMK: 'Account at MoneyKeeper',
        noAccountsInCurrency: 'You do not have an account suitable for the currency'
      },
      ru: {
        addAsNew: 'Добавить как новый',
        joinWithExisting: 'Объединить с уже существующим',
        accountLabel: 'Счет',
        accountLabelAtMK: 'Счет в MoneyKeeper',
        noAccountsInCurrency: 'У вас нет счета подходящего по валюте'
      }
    }
  }
}
</script>

<style lang="scss">
.manage-item-grid {
  @apply grid mb-8;
  grid-template-columns: 60px auto 180px 230px;
  grid-row-gap: 17px;
  align-items: center;

  .select {
    grid-column: 4/5;
  }
}
</style>